import { useCallback, useState } from 'react'
import { userService } from '../services/userService'

interface ContactInfo {
  email: string
  name?: string
  phoneNumber?: string
}

interface UseSaveToDirectoryReturn {
  /** Whether the save-to-directory dialog should be shown */
  showDialog: boolean
  /** The contact info to potentially save */
  pendingContact: ContactInfo | null
  /** Whether we're currently checking if contact exists */
  isChecking: boolean
  /** Check if contact should be offered to save to directory */
  checkAndPrompt: (contact: ContactInfo) => Promise<boolean>
  /** Handle user confirming save to directory */
  handleConfirm: () => void
  /** Handle user canceling save to directory */
  handleCancel: () => void
  /** Reset the state */
  reset: () => void
}

/**
 * Hook to manage the "save to directory" workflow.
 *
 * When contact information is entered and saved, this hook helps check if
 * the contact is already in the user's directory. If not, it can prompt
 * the user to save it.
 *
 * @example
 * const { showDialog, pendingContact, checkAndPrompt, handleConfirm, handleCancel } = useSaveToDirectory()
 *
 * // After saving contact info:
 * const shouldPrompt = await checkAndPrompt({ email: 'user@example.com', name: 'John Doe' })
 * if (shouldPrompt) {
 *   // Dialog will be shown automatically via showDialog state
 * }
 */
export function useSaveToDirectory(): UseSaveToDirectoryReturn {
  const [showDialog, setShowDialog] = useState(false)
  const [pendingContact, setPendingContact] = useState<ContactInfo | null>(null)
  const [isChecking, setIsChecking] = useState(false)

  const checkAndPrompt = useCallback(
    async (contact: ContactInfo): Promise<boolean> => {
      if (!contact.email) {
        return false
      }

      setIsChecking(true)

      try {
        const result = await userService.checkContactExists(contact.email)

        // If user exists but is not in directory, prompt to add
        if (result.user_exists && !result.exists) {
          setPendingContact(contact)
          setShowDialog(true)
          return true
        }

        // Already in directory or user doesn't exist
        return false
      } catch (error) {
        console.error('Failed to check if contact exists:', error)
        return false
      } finally {
        setIsChecking(false)
      }
    },
    [],
  )

  const handleConfirm = useCallback(() => {
    setShowDialog(false)
    setPendingContact(null)
  }, [])

  const handleCancel = useCallback(() => {
    setShowDialog(false)
    setPendingContact(null)
  }, [])

  const reset = useCallback(() => {
    setShowDialog(false)
    setPendingContact(null)
    setIsChecking(false)
  }, [])

  return {
    showDialog,
    pendingContact,
    isChecking,
    checkAndPrompt,
    handleConfirm,
    handleCancel,
    reset,
  }
}

export default useSaveToDirectory
